/**
 * Sitzungszustand fuer die ganze App.
 *
 * WARUM EIN EIGENER ZUSTAND „laedt": Beim Start liest der Client die Sitzung
 * aus dem Speicher und loest, falls vorhanden, einen Code aus der Adresse ein.
 * Beides ist asynchron. Ohne den Zwischenzustand wuerde jede geschuetzte Seite
 * fuer einen Lidschlag „nicht angemeldet" sehen und den Nutzer aus seiner
 * gerade bestaetigten Sitzung auf die Anmeldung werfen.
 *
 * WARUM DAS LETZTE EREIGNIS GEMERKT WIRD: Ein Wiederherstellungslink erzeugt
 * eine gueltige Sitzung, ist aber keine normale Anmeldung — der Nutzer soll
 * genau ein Passwort setzen und nicht in der App landen. Das unterscheidet
 * sich nur am Ereignis PASSWORD_RECOVERY.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

interface SitzungsLage {
  status: 'laedt' | 'angemeldet' | 'abgemeldet';
  session: Session | null;
  /** true, solange die Sitzung aus einem Wiederherstellungslink stammt. */
  wiederherstellung: boolean;
  abmelden: () => Promise<void>;
}

const Kontext = createContext<SitzungsLage | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [wiederherstellung, setWiederherstellung] = useState(false);

  useEffect(() => {
    let lebt = true;

    // Erst der gespeicherte Stand, damit ein Neuladen nicht abmeldet.
    supabase.auth.getSession().then(({ data }) => {
      if (!lebt) return;
      setSession(data.session);
      setLaedt(false);
    });

    // Danach jede Aenderung: Anmeldung, Abmeldung, Token-Erneuerung, und der
    // Einloesung eines Links aus einer Mail.
    const { data: sub } = supabase.auth.onAuthStateChange((ereignis: AuthChangeEvent, s) => {
      if (!lebt) return;
      if (ereignis === 'PASSWORD_RECOVERY') setWiederherstellung(true);
      if (ereignis === 'SIGNED_OUT') setWiederherstellung(false);
      setSession(s);
      setLaedt(false);
    });

    return () => {
      lebt = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const wert: SitzungsLage = {
    status: laedt ? 'laedt' : session ? 'angemeldet' : 'abgemeldet',
    session,
    wiederherstellung,
    abmelden: async () => {
      await supabase.auth.signOut();
      setWiederherstellung(false);
    },
  };

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

export function useSitzung(): SitzungsLage {
  const s = useContext(Kontext);
  if (!s) throw new Error('useSitzung ausserhalb von SessionProvider benutzt.');
  return s;
}

/** Wie useSitzung, aber ohne Fehler ausserhalb des Providers (Umschalter in Tests und Vorschauen). */
export function useSitzungOderNull(): SitzungsLage | null {
  return useContext(Kontext);
}
